import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import type { ResourceType } from 'types/resource-types';
import { type ApiError, type ApiResponse, useAxiosInstance } from './utils';
import { addActionNameToMutationResult } from './utils/add-action-name-to-mutation-result';

export type DuplicateViewVariables = {
  viewId: string;
  resourceType: ResourceType;
};

export async function duplicateViewFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: DuplicateViewVariables,
): Promise<ApiResponse> {
  const { viewId } = variables;
  const instance = await axiosInstance;
  return instance.post(`views/${viewId}/duplicate`);
}

export function useDuplicateView() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, DuplicateViewVariables>({
    mutationFn: variables => duplicateViewFn(axiosInstance, variables),
    onSuccess: (_response, { resourceType }) => {
      client.invalidateQueries({
        queryKey: ['views', resourceType],
      });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    DuplicateViewVariables,
    'duplicateView'
  >('duplicateView', mutation);
}
